import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Grid,
  Typography,
  Button,
  Box,
  CircularProgress,
  Alert,
  Fab,
  List
} from '@mui/material';
import { Add as AddIcon, KeyboardArrowUp as KeyboardArrowUpIcon } from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { bookService } from '../../services/bookService';
import { currencyService } from '../../services/currencyService';
import { useTranslation } from '../../hooks/useTranslation';

// Import smaller components
import BookListHeader from './BookListHeader';
import BookListControls from './BookListControls';
import BookCard from './BookCard';
import BookListItem from './BookListItem';
import BookListFooter from './BookListFooter';
import DeleteConfirmationDialog from './DeleteConfirmationDialog';

function BookList() {
  const [allBooks, setAllBooks] = useState([]);
  const [displayedBooks, setDisplayedBooks] = useState([]);
  const [filteredBooks, setFilteredBooks] = useState([]);
  const [searchTerm, setSearchTerm] = useState(() => {
    return localStorage.getItem('bookport_searchTerm') || '';
  });
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [bookToDelete, setBookToDelete] = useState(null);
  const [viewMode, setViewMode] = useState(() => {
    return localStorage.getItem('bookport_viewMode') || 'grid';
  });
  const [userCurrency, setUserCurrency] = useState('USD');
  const [showScrollTop, setShowScrollTop] = useState(false);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const ITEMS_PER_PAGE = 20;
  
  const { currentUser } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();

  // Helper function to get image URL with fallback
  const getImageUrl = (imageUrl, bookName) => {
    if (imageUrl) {
      return imageUrl;
    }
    const encodedName = encodeURIComponent(bookName || 'Book');
    return `https://placehold.co/400x300/e0e0e0/666666?text=${encodedName}`;
  };

  useEffect(() => {
    loadBooks();
    loadUserCurrency();
    const savedScrollPosition = localStorage.getItem('bookport_scrollPosition');
    if (savedScrollPosition) {
      setTimeout(() => {
        window.scrollTo(0, parseInt(savedScrollPosition));
      }, 100);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    localStorage.setItem('bookport_searchTerm', searchTerm);
  }, [searchTerm]);

  useEffect(() => {
    localStorage.setItem('bookport_viewMode', viewMode);
  }, [viewMode]);

  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 400);
      
      if (
        !loadingMore &&
        hasMore &&
        window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 1000
      ) {
        loadMoreItems();
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingMore, hasMore, filteredBooks.length, currentPage]);

  useEffect(() => {
    if (searchTerm) {
      const filtered = allBooks.filter(book =>
        book.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        book.isbn?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        book.publisher?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        book.author?.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredBooks(filtered);
      setCurrentPage(1);
      setDisplayedBooks(filtered.slice(0, ITEMS_PER_PAGE));
      setHasMore(filtered.length > ITEMS_PER_PAGE);
    } else {
      setFilteredBooks(allBooks);
      setCurrentPage(1);
      setDisplayedBooks(allBooks.slice(0, ITEMS_PER_PAGE));
      setHasMore(allBooks.length > ITEMS_PER_PAGE);
    }
  }, [searchTerm, allBooks]);

  async function loadBooks() {
    if (!currentUser?.uid) return;
    
    try {
      setLoading(true);
      const booksData = await bookService.getBooks(currentUser.uid);
      setAllBooks(booksData);
      setFilteredBooks(booksData);
      setDisplayedBooks(booksData.slice(0, ITEMS_PER_PAGE));
      setHasMore(booksData.length > ITEMS_PER_PAGE);
      setCurrentPage(1);
    } catch (error) {
      setError('Failed to load books: ' + error.message);
    } finally {
      setLoading(false);
    }
  }

  async function loadMoreItems() {
    if (loadingMore || !hasMore) return;
    
    try {
      setLoadingMore(true);
      await new Promise(resolve => setTimeout(resolve, 300));
      
      const nextPage = currentPage + 1;
      const startIndex = (nextPage - 1) * ITEMS_PER_PAGE;
      const endIndex = startIndex + ITEMS_PER_PAGE;
      const newItems = filteredBooks.slice(startIndex, endIndex);
      
      setDisplayedBooks(prev => [...prev, ...newItems]);
      setCurrentPage(nextPage);
      setHasMore(endIndex < filteredBooks.length);
    } catch (error) {
      console.error('Failed to load more items:', error);
    } finally {
      setLoadingMore(false);
    }
  }

  async function loadUserCurrency() {
    if (!currentUser?.uid) return;
    
    try {
      const currency = await currencyService.getUserCurrency(currentUser.uid);
      setUserCurrency(currency);
    } catch (error) {
      console.error('Failed to load user currency:', error);
    }
  }

  async function handleDelete(bookId) {
    if (!currentUser?.uid) return;
    
    try {
      await bookService.deleteBook(currentUser.uid, bookId);
      setAllBooks(prevBooks => prevBooks.filter(book => book.id !== bookId));
      setFilteredBooks(prevBooks => prevBooks.filter(book => book.id !== bookId));
      setDisplayedBooks(prevBooks => prevBooks.filter(book => book.id !== bookId));
      setDeleteDialogOpen(false);
      setBookToDelete(null);
    } catch (error) {
      setError('Failed to delete book: ' + error.message);
    }
  }

  async function handleClone(book) {
    if (!currentUser?.uid) return;
    
    try {
      // Create a clone of the book data, removing the id and timestamps
      const clonedBook = {
        title: `${book.title} (Copy)`,
        author: book.author || '',
        isbn: book.isbn || '',
        publisher: book.publisher || '',
        genre: book.genre || '',
        type: book.type || '',
        language: book.language || '',
        imageUrl: book.imageUrl || '',
        qty: book.qty || 1,
        price: book.price || 0,
        source: book.source || '',
        note: book.note || '-',
        createdDate: book.createdDate || ''
      };

      // Save the cloned book
      await bookService.createBook(clonedBook, currentUser.uid);
      
      // Reload books to show the new clone
      await loadBooks();
      
      // Show success message
      setError(''); // Clear any existing errors
      // You could add a success state here if needed
    } catch (error) {
      setError('Failed to clone book: ' + error.message);
    }
  }

  function handleEditBook(bookId) {
    localStorage.setItem('bookport_scrollPosition', window.scrollY.toString());
    navigate(`/book/${bookId}`);
  }

  function handleAddNewBook() {
    localStorage.setItem('bookport_scrollPosition', window.scrollY.toString());
    navigate('/book/new');
  }

  function handleGoToInsights() {
    navigate('/insights');
  }

  function scrollToTop() {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  function openDeleteDialog(book) {
    setBookToDelete(book);
    setDeleteDialogOpen(true);
  }

  function handleSearchChange(e) {
    setSearchTerm(e.target.value);
  }

  function handleClearSearch() {
    setSearchTerm('');
  }

  function handleViewModeChange(mode) {
    setViewMode(mode);
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <BookListHeader 
        onAddNewBook={handleAddNewBook}
        onGoToInsights={handleGoToInsights}
      />

      <Container maxWidth="lg" sx={{ py: 4 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        <BookListControls
          searchTerm={searchTerm}
          onSearchChange={handleSearchChange}
          onClearSearch={handleClearSearch}
          viewMode={viewMode}
          onViewModeChange={handleViewModeChange}
          filteredBooks={filteredBooks}
          userCurrency={userCurrency}
        />

        {/* Books Grid/List */}
        {displayedBooks.length === 0 ? (
          <Box sx={{
            background: 'linear-gradient(135deg, #ffffff 0%, #fafafa 100%)',
            p: 6,
            borderRadius: 3,
            textAlign: 'center',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
          }}>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
              {searchTerm ? t('collection.noSearchResults') : t('collection.noBooks')}
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              {searchTerm ? t('collection.tryAdjustingSearch') : t('collection.startBuildingCollection')}
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAddNewBook}
              size="large"
            >
              {t('collection.addFirstBook')}
            </Button>
          </Box>
        ) : (
          <>
            {viewMode === 'grid' ? (
              <Grid container spacing={3}>
                {displayedBooks.map((book) => (
                  <BookCard
                    key={book.id}
                    book={book}
                    userCurrency={userCurrency}
                    onEdit={handleEditBook}
                    onDelete={openDeleteDialog}
                    onClone={handleClone}
                    getImageUrl={getImageUrl}
                  />
                ))}
              </Grid>
            ) : (
              <List>
                {displayedBooks.map((book) => (
                  <BookListItem
                    key={book.id}
                    book={book}
                    userCurrency={userCurrency}
                    onEdit={handleEditBook}
                    onDelete={openDeleteDialog}
                    onClone={handleClone}
                    getImageUrl={getImageUrl}
                  />
                ))}
              </List>
            )}

            {/* Loading More Indicator */}
            {loadingMore && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                <CircularProgress size={40} />
              </Box>
            )}

            {/* End of Results Indicator */}
            {!hasMore && displayedBooks.length > 0 && (
              <Box sx={{ textAlign: 'center', mt: 4, py: 3 }}>
                <Typography variant="body2" color="text.secondary">
                  {t('collection.endOfResults')}
                </Typography>
              </Box>
            )}
          </>
        )}
      </Container>

      <BookListFooter />

      {/* Scroll to Top Button */}
      {showScrollTop && (
        <Fab
          color="primary"
          aria-label="scroll to top"
          onClick={scrollToTop}
          sx={{
            position: 'fixed',
            bottom: 16,
            right: 16,
            zIndex: 1000
          }}
        >
          <KeyboardArrowUpIcon />
        </Fab>
      )}

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmationDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={() => handleDelete(bookToDelete?.id)}
      />
    </Box>
  );
}

export default BookList; 